import React, { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { MapContainer, TileLayer, Marker, Popup, Circle } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { 
  HeartHandshake, 
  Cat, 
  UserCheck, 
  Activity, 
  BellRing,
  Shield,
  MapPin,
  Trophy,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Clock,
  Star,
  Users,
  Locate,
  Eye,
  EyeOff,
  Award,
  CheckCircle2,
  Loader2,
  Camera,
  UserSearch,
  FileText,
  X,
  Check,
  AlertCircle,
  Upload,
  RefreshCw
} from "lucide-react";

// Fix Leaflet icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const protecteurIcon = new L.DivIcon({
  html: `<div style="background: linear-gradient(135deg, #22C55E, #16A34A); width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 3px 10px rgba(34,197,94,0.5);">
    <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
  </div>`,
  className: '',
  iconSize: [36, 36],
  iconAnchor: [18, 18],
});

const DEED_ICONS = {
  aide_personne: HeartHandshake,
  animal_retrouve: Cat,
  personne_retrouvee: UserCheck,
  secours: Activity,
  signalement_utile: BellRing
};

const VALIDATION_ICONS = {
  photo_proof: Camera,
  crossed_profile: UserSearch,
  self_declaration: FileText
};

const GoodDeedsPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [protecteurs, setProtecteurs] = useState([]);
  const [validationMethods, setValidationMethods] = useState([]);
  
  // Form state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [description, setDescription] = useState("");
  const [userLocation, setUserLocation] = useState(null);
  const [shareLocation, setShareLocation] = useState(false);
  const [selectedValidation, setSelectedValidation] = useState("self_declaration");
  
  // Crossed profiles state
  const [crossedProfiles, setCrossedProfiles] = useState([]);
  const [loadingCrossed, setLoadingCrossed] = useState(false);
  const [selectedHelpedUser, setSelectedHelpedUser] = useState(null);
  const [crossedIndex, setCrossedIndex] = useState(0);
  
  // Photo proof state
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoConsent, setPhotoConsent] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // View state
  const [activeTab, setActiveTab] = useState("declare");
  const [mapCenter, setMapCenter] = useState([46.603354, 1.888334]);
  const [formStep, setFormStep] = useState(1); // 1: category, 2: validation method, 3: details
  
  const fileInputRef = useRef(null);

  const getUserLocation = useCallback(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = [pos.coords.latitude, pos.coords.longitude];
          setUserLocation(loc);
          setMapCenter(loc);
        },
        () => setMapCenter([48.8566, 2.3522])
      );
    }
  }, []);

  const startLocationTracking = useCallback(() => {
    // Update location every 2 minutes for crossed profiles
    const updateLocation = async () => {
      if (!navigator.geolocation || !token) return;
      
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          await axios.post(`${API}/good-deeds/update-location`, {
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude
          }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (e) {
          console.log("Location update failed:", e);
        }
      });
    };
    
    updateLocation();
    const interval = setInterval(updateLocation, 120000); // 2 minutes
    return () => clearInterval(interval);
  }, [token]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [catRes, statsRes, leaderRes, methodsRes] = await Promise.all([
        axios.get(`${API}/good-deeds/categories`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/good-deeds/my-stats`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/good-deeds/leaderboard?limit=10`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/good-deeds/validation-methods`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setCategories(catRes.data.categories);
      setStats(statsRes.data);
      setLeaderboard(leaderRes.data.leaderboard);
      setValidationMethods(methodsRes.data.methods);
      
      if (userLocation) {
        const protRes = await axios.get(
          `${API}/good-deeds/protecteurs?lat=${userLocation[0]}&lng=${userLocation[1]}&radius_km=20`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProtecteurs(protRes.data.protecteurs);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [token, userLocation]);

  // Initial data fetch
  useEffect(() => {
    fetchData();
    getUserLocation();
    const cleanup = startLocationTracking();
    return cleanup;
  }, [fetchData, getUserLocation, startLocationTracking]);

  const fetchCrossedProfiles = async () => {
    if (!userLocation) {
      toast.error("Position GPS requise");
      return;
    }
    
    setLoadingCrossed(true);
    try {
      const res = await axios.get(
        `${API}/good-deeds/crossed-profiles?lat=${userLocation[0]}&lng=${userLocation[1]}&minutes=30`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCrossedProfiles(res.data.profiles);
      setCrossedIndex(0);
      if (res.data.profiles.length === 0) {
        toast.info("Aucun profil croisé dans les 30 dernières minutes");
      }
    } catch (error) {
      toast.error("Erreur lors de la recherche des profils");
    } finally {
      setLoadingCrossed(false);
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("La photo ne doit pas dépasser 5MB");
        return;
      }
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPhotoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile) return null;
    
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("file", photoFile);
      
      const res = await axios.post(`${API}/upload/image`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      return res.data.url;
    } catch (error) {
      toast.error("Erreur lors de l'upload de la photo");
      return null;
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmitDeed = async () => {
    if (!selectedCategory) {
      toast.error("Sélectionnez un type de bonne action");
      return;
    }
    if (description.length < 20) {
      toast.error("La description doit faire au moins 20 caractères");
      return;
    }
    
    // Validation checks
    if (selectedValidation === "crossed_profile" && !selectedHelpedUser) {
      toast.error("Sélectionnez la personne que vous avez aidée");
      return;
    }
    if (selectedValidation === "photo_proof" && !photoFile) {
      toast.error("Ajoutez une photo comme preuve");
      return;
    }

    setSubmitting(true);
    try {
      let photoUrl = null;
      if (selectedValidation === "photo_proof" && photoFile) {
        photoUrl = await uploadPhoto();
      }

      const response = await axios.post(
        `${API}/good-deeds`,
        {
          deed_type: selectedCategory.id,
          description,
          latitude: userLocation?.[0],
          longitude: userLocation?.[1],
          validation_method: selectedValidation,
          helped_user_id: selectedHelpedUser?.id || null,
          photo_proof_url: photoUrl,
          photo_consent: photoConsent
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.badge_earned) {
        toast.success("Félicitations ! Vous avez obtenu le badge PROTECTEUR !", { duration: 5000 });
      } else {
        toast.success(response.data.message);
      }

      // Reset form
      setSelectedCategory(null);
      setDescription("");
      setSelectedValidation("self_declaration");
      setSelectedHelpedUser(null);
      setPhotoFile(null);
      setPhotoPreview(null);
      setPhotoConsent(false);
      setCrossedProfiles([]);
      setFormStep(1);
      
      fetchData();
      setActiveTab("stats");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSubmitting(false);
    }
  };

  const toggleShareLocation = async () => {
    try {
      const newShare = !shareLocation;
      await axios.post(
        `${API}/good-deeds/share-location`,
        { share: newShare, lat: userLocation?.[0], lng: userLocation?.[1] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setShareLocation(newShare);
      toast.success(newShare ? "Position partagée sur la carte" : "Position masquée");
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const nextCrossedProfile = () => {
    if (crossedIndex < crossedProfiles.length - 1) {
      setCrossedIndex(crossedIndex + 1);
    }
  };

  const prevCrossedProfile = () => {
    if (crossedIndex > 0) {
      setCrossedIndex(crossedIndex - 1);
    }
  };

  const selectCrossedProfile = (profile) => {
    setSelectedHelpedUser(profile);
    toast.success(`${profile.full_name} sélectionné`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="animate-spin text-[#FF3366]" size={48} />
        </div>
      </Layout>
    );
  }

  const currentCrossedProfile = crossedProfiles[crossedIndex];

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-3">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center shadow-lg">
              <Shield size={32} className="text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Bonnes Actions</h1>
          <p className="text-slate-500">Aidez votre communauté et devenez Protecteur</p>
        </div>

        {/* Stats Banner */}
        {stats && (
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">{stats.total_points}</div>
                    <div className="text-xs text-green-700">Points</div>
                  </div>
                  <div className="h-12 w-px bg-green-200" />
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">{stats.total_deeds}</div>
                    <div className="text-xs text-green-700">Actions</div>
                  </div>
                </div>
                
                {stats.has_protecteur ? (
                  <div className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-xl">
                    <Shield size={20} />
                    <div>
                      <div className="font-bold text-sm">PROTECTEUR</div>
                      <div className="text-xs opacity-90">{stats.protecteur_days_remaining}j restants</div>
                    </div>
                  </div>
                ) : (
                  <div className="text-right">
                    <div className="text-sm text-slate-600">
                      <span className="font-bold text-green-600">{stats.points_to_protecteur}</span> pts pour badge
                    </div>
                    <Progress value={(stats.total_points / 100) * 100} className="w-32 h-2 mt-1" />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <div className="flex bg-slate-100 rounded-xl p-1 gap-1">
          {[
            { id: "declare", label: "Déclarer", icon: HeartHandshake },
            { id: "stats", label: "Mes Actions", icon: Trophy },
            { id: "map", label: "Carte", icon: MapPin },
            { id: "leaderboard", label: "Classement", icon: Star }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id ? "bg-white text-green-600 shadow" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                <Icon size={16} />
                <span className="hidden sm:inline">{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Declare Tab */}
        {activeTab === "declare" && (
          <div className="space-y-4">
            {/* Step 1: Category Selection */}
            {formStep === 1 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span className="w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">1</span>
                    Quelle bonne action avez-vous faite ?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {categories.map(cat => {
                      const Icon = DEED_ICONS[cat.id] || HeartHandshake;
                      const isSelected = selectedCategory?.id === cat.id;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => setSelectedCategory(cat)}
                          className={`p-4 rounded-xl border-2 text-left transition-all ${
                            isSelected ? "border-green-500 bg-green-50" : "border-slate-200 hover:border-green-300"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: cat.color + "20" }}>
                              <Icon size={20} style={{ color: cat.color }} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-slate-800">{cat.label}</div>
                              <div className="text-xs text-slate-500 truncate">{cat.description}</div>
                              <div className="mt-1 text-xs font-bold" style={{ color: cat.color }}>+{cat.points} points</div>
                            </div>
                            {isSelected && <CheckCircle2 className="text-green-500 flex-shrink-0" size={20} />}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  
                  {selectedCategory && (
                    <Button onClick={() => setFormStep(2)} className="w-full bg-green-500 hover:bg-green-600">
                      Continuer <ChevronRight size={18} />
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Step 2: Validation Method */}
            {formStep === 2 && (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">2</span>
                      Comment valider votre action ?
                    </CardTitle>
                    <button onClick={() => setFormStep(1)} className="text-slate-400 hover:text-slate-600">
                      <ChevronLeft size={20} />
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {validationMethods.map(method => {
                      const Icon = VALIDATION_ICONS[method.id] || FileText;
                      const isSelected = selectedValidation === method.id;
                      return (
                        <button
                          key={method.id}
                          onClick={() => setSelectedValidation(method.id)}
                          className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                            isSelected ? "border-green-500 bg-green-50" : "border-slate-200 hover:border-green-300"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isSelected ? "bg-green-500 text-white" : "bg-slate-100 text-slate-500"}`}>
                              <Icon size={24} />
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-slate-800">{method.label}</div>
                              <div className="text-xs text-slate-500">{method.description}</div>
                              {method.bonus_points > 0 && (
                                <div className="text-xs font-bold text-green-600 mt-1">+{method.bonus_points} pts bonus</div>
                              )}
                            </div>
                            {isSelected && <CheckCircle2 className="text-green-500" size={20} />}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Crossed Profiles Swiper */}
                  {selectedValidation === "crossed_profile" && (
                    <div className="space-y-3 p-4 bg-slate-50 rounded-xl">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-slate-700">Profils croisés (30 min)</span>
                        <button
                          onClick={fetchCrossedProfiles}
                          disabled={loadingCrossed}
                          className="flex items-center gap-1 text-sm text-green-600 font-medium"
                        >
                          {loadingCrossed ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
                          Actualiser
                        </button>
                      </div>
                      
                      {crossedProfiles.length > 0 ? (
                        <div className="relative">
                          <div className="flex items-center justify-center gap-4">
                            <button
                              onClick={prevCrossedProfile}
                              disabled={crossedIndex === 0}
                              className="w-10 h-10 rounded-full bg-white border shadow flex items-center justify-center disabled:opacity-30"
                            >
                              <ChevronLeft size={20} />
                            </button>
                            
                            {currentCrossedProfile && (
                              <div 
                                className={`flex-1 max-w-xs p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                                  selectedHelpedUser?.id === currentCrossedProfile.id 
                                    ? "border-green-500 bg-green-50" 
                                    : "border-slate-200 bg-white"
                                }`}
                                onClick={() => selectCrossedProfile(currentCrossedProfile)}
                              >
                                <div className="flex flex-col items-center text-center">
                                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-200 to-pink-100 flex items-center justify-center overflow-hidden mb-3">
                                    {currentCrossedProfile.profile_photo ? (
                                      <img src={currentCrossedProfile.profile_photo} alt="" className="w-full h-full object-cover" />
                                    ) : (
                                      <Users size={32} className="text-[#FF3366]" />
                                    )}
                                  </div>
                                  <div className="font-bold text-slate-800">{currentCrossedProfile.full_name}</div>
                                  <div className="text-xs text-slate-500">Niveau: {currentCrossedProfile.level}</div>
                                  <div className="text-xs text-green-600 mt-1">
                                    <Clock size={12} className="inline mr-1" />
                                    Croisé il y a {Math.round((Date.now() - new Date(currentCrossedProfile.crossed_at).getTime()) / 60000)} min
                                  </div>
                                  {selectedHelpedUser?.id === currentCrossedProfile.id && (
                                    <div className="mt-2 text-green-600 font-medium flex items-center gap-1">
                                      <Check size={16} /> Sélectionné
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            <button
                              onClick={nextCrossedProfile}
                              disabled={crossedIndex >= crossedProfiles.length - 1}
                              className="w-10 h-10 rounded-full bg-white border shadow flex items-center justify-center disabled:opacity-30"
                            >
                              <ChevronRight size={20} />
                            </button>
                          </div>
                          <div className="text-center text-xs text-slate-400 mt-2">
                            {crossedIndex + 1} / {crossedProfiles.length} profils
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-6 text-slate-500">
                          <UserSearch size={32} className="mx-auto mb-2 text-slate-300" />
                          <p className="text-sm">Cliquez sur "Actualiser" pour voir les profils croisés</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Photo Upload */}
                  {selectedValidation === "photo_proof" && (
                    <div className="space-y-3 p-4 bg-slate-50 rounded-xl">
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handlePhotoSelect}
                        accept="image/*"
                        className="hidden"
                      />
                      
                      {!photoPreview ? (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="w-full h-40 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-green-400 transition-colors"
                        >
                          <Camera size={32} className="text-slate-400" />
                          <span className="text-sm text-slate-500">Ajouter une photo</span>
                          <span className="text-xs text-slate-400">Max 5MB</span>
                        </button>
                      ) : (
                        <div className="relative">
                          <img src={photoPreview} alt="Preview" className="w-full h-40 object-cover rounded-xl" />
                          <button
                            onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500 text-white flex items-center justify-center"
                          >
                            <X size={16} />
                          </button>
                        </div>
                      )}
                      
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={photoConsent}
                          onChange={(e) => setPhotoConsent(e.target.checked)}
                          className="mt-1"
                        />
                        <span className="text-xs text-slate-600">
                          Je confirme que cette photo ne montre pas de visage identifiable ou que j'ai obtenu le consentement de la personne photographiée (RGPD)
                        </span>
                      </label>
                    </div>
                  )}

                  <Button onClick={() => setFormStep(3)} className="w-full bg-green-500 hover:bg-green-600">
                    Continuer <ChevronRight size={18} />
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Description & Submit */}
            {formStep === 3 && (
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-green-500 text-white text-xs flex items-center justify-center">3</span>
                      Décrivez votre action
                    </CardTitle>
                    <button onClick={() => setFormStep(2)} className="text-slate-400 hover:text-slate-600">
                      <ChevronLeft size={20} />
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Summary */}
                  <div className="p-3 bg-green-50 rounded-xl flex items-center gap-3">
                    {selectedCategory && (
                      <>
                        {React.createElement(DEED_ICONS[selectedCategory.id] || HeartHandshake, { size: 20, style: { color: selectedCategory.color } })}
                        <span className="font-medium text-slate-700">{selectedCategory.label}</span>
                        <span className="ml-auto text-sm text-green-600 font-bold">+{selectedCategory.points} pts</span>
                      </>
                    )}
                  </div>

                  {selectedHelpedUser && (
                    <div className="p-3 bg-blue-50 rounded-xl flex items-center gap-3">
                      <UserCheck size={20} className="text-blue-500" />
                      <span className="text-sm text-slate-700">Personne aidée: <strong>{selectedHelpedUser.full_name}</strong></span>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Description (min. 20 caractères)</label>
                    <Textarea
                      placeholder="Décrivez ce que vous avez fait..."
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      className="resize-none"
                    />
                    <div className="text-xs text-slate-400 text-right">{description.length}/500</div>
                  </div>

                  {userLocation && (
                    <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-xl">
                      <MapPin size={18} className="text-green-500" />
                      <span className="text-sm text-slate-600 flex-1">Position GPS détectée</span>
                      <CheckCircle2 size={18} className="text-green-500" />
                    </div>
                  )}

                  <Button
                    onClick={handleSubmitDeed}
                    disabled={description.length < 20 || submitting || (selectedValidation === "photo_proof" && (!photoFile || !photoConsent))}
                    className="w-full h-12 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-bold rounded-xl"
                  >
                    {submitting ? (
                      <><Loader2 className="animate-spin mr-2" size={20} /> Enregistrement...</>
                    ) : (
                      <><Award className="mr-2" size={20} /> Enregistrer ma bonne action</>
                    )}
                  </Button>

                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm">
                    <div className="font-medium text-amber-800 mb-1">Charte RGPD</div>
                    <p className="text-amber-700 text-xs">
                      Vos bonnes actions respectent le RGPD. Les photos sans consentement explicite sont interdites. 
                      Maximum 3 déclarations par jour.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === "stats" && stats && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Mes dernières actions</CardTitle>
              </CardHeader>
              <CardContent>
                {stats.recent_deeds?.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_deeds.map(deed => {
                      const Icon = DEED_ICONS[deed.deed_type] || HeartHandshake;
                      const cat = categories.find(c => c.id === deed.deed_type);
                      const isPending = deed.status === "pending_validation" || deed.status === "pending_review";
                      return (
                        <div key={deed.id} className={`flex items-start gap-3 p-3 rounded-xl ${isPending ? "bg-amber-50 border border-amber-200" : "bg-slate-50"}`}>
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: (cat?.color || "#22C55E") + "20" }}>
                            <Icon size={20} style={{ color: cat?.color || "#22C55E" }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-slate-800">{deed.deed_label}</span>
                              {isPending && (
                                <span className="text-xs px-2 py-0.5 bg-amber-200 text-amber-800 rounded-full">En attente</span>
                              )}
                            </div>
                            <div className="text-sm text-slate-500 line-clamp-2">{deed.description}</div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-green-600 font-bold">+{deed.points_earned} pts</span>
                              {deed.bonus_points_pending > 0 && (
                                <span className="text-xs text-amber-600">(+{deed.bonus_points_pending} si validé)</span>
                              )}
                              <span className="text-xs text-slate-400">
                                {new Date(deed.created_at).toLocaleDateString('fr-FR')}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <HeartHandshake size={48} className="mx-auto mb-2 text-slate-300" />
                    <p>Aucune bonne action enregistrée</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Map Tab */}
        {activeTab === "map" && (
          <div className="space-y-4">
            {stats?.has_protecteur && (
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Shield className="text-green-600" size={24} />
                      <div>
                        <div className="font-medium text-green-800">Vous êtes Protecteur</div>
                        <div className="text-sm text-green-600">Partagez votre position pour aider</div>
                      </div>
                    </div>
                    <button
                      onClick={toggleShareLocation}
                      className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                        shareLocation ? "bg-green-500 text-white" : "bg-white text-slate-600 border"
                      }`}
                    >
                      {shareLocation ? <Eye size={18} /> : <EyeOff size={18} />}
                      {shareLocation ? "Visible" : "Masqué"}
                    </button>
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="overflow-hidden">
              <div className="h-[400px]">
                <MapContainer center={mapCenter} zoom={12} style={{ height: "100%", width: "100%" }}>
                  <TileLayer attribution='&copy; OSM' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  {userLocation && <Circle center={userLocation} radius={500} pathOptions={{ color: '#FF3366', fillColor: '#FF3366', fillOpacity: 0.1 }} />}
                  {protecteurs.map(p => (
                    <Marker key={p.id} position={[p.latitude, p.longitude]} icon={protecteurIcon}>
                      <Popup>
                        <div className="text-center">
                          <div className="font-bold text-green-600">{p.full_name}</div>
                          <div className="text-xs text-slate-500">Protecteur</div>
                          <div className="text-sm font-bold mt-1">{p.good_deed_points} pts</div>
                        </div>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              </div>
              <div className="p-3 bg-slate-50 border-t flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 rounded-full bg-gradient-to-br from-green-400 to-green-600" />
                  <span className="text-sm text-slate-600">Protecteurs actifs: {protecteurs.length}</span>
                </div>
                <button onClick={() => userLocation && setMapCenter(userLocation)} className="flex items-center gap-1 text-sm text-[#FF3366] font-medium">
                  <Locate size={16} /> Ma position
                </button>
              </div>
            </Card>
          </div>
        )}

        {/* Leaderboard Tab */}
        {activeTab === "leaderboard" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Trophy className="text-amber-500" size={20} />
                Top Protecteurs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {leaderboard.map((user, index) => (
                  <div key={user.id} className={`flex items-center gap-3 p-3 rounded-xl ${index < 3 ? "bg-gradient-to-r from-amber-50 to-yellow-50" : "bg-slate-50"}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? "bg-amber-500 text-white" :
                      index === 1 ? "bg-slate-400 text-white" :
                      index === 2 ? "bg-amber-700 text-white" : "bg-slate-200 text-slate-600"
                    }`}>
                      {index + 1}
                    </div>
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-200 to-pink-100 flex items-center justify-center overflow-hidden">
                      {user.profile_photo ? <img src={user.profile_photo} alt="" className="w-full h-full object-cover" /> : <Users size={20} className="text-[#FF3366]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-800 truncate">{user.full_name}</span>
                        {user.has_protecteur && <Shield size={16} className="text-green-500 flex-shrink-0" />}
                      </div>
                      <div className="text-xs text-slate-500">Niveau: {user.level}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-green-600">{user.good_deed_points}</div>
                      <div className="text-xs text-slate-400">points</div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
};

export default GoodDeedsPage;
